import { ActionReference, DefaultGameState, GameObjectReference, GameState } from "@shared/types";
import { css, html, htmlArray } from "../helpers/webComponents";
import { GameEventService, UpdateGameStateEvent } from "../services/GameEventService";
import { GameRouteService } from "../services/GameRouteService";
import { Page } from "../enums/Page";
import bbobHTML from "@bbob/html";
import presetHTML5 from "@bbob/preset-html5";
import { BBobPlugins } from "@bbob/types";
import { GameEvent } from "../enums/GameEvent";
import { SoundComponents } from "./SoundComponents";

/** CSS affecting the {@link CanvasComponent} */
const defaultStyles: string = css`
    :host {
        font-family: monospace;
        width: 100%;
        height: 100%;
        background-size: cover;

        --box-shadow: 0 0 20px rgba(0, 0, 0, 0.5);
    }
    
    .wrapper {
        width: 100%;
        height: 100%;
        max-width: 1024px;
        margin: auto;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
    }
    
    .title {
        text-align: center;
        margin-top: 10px;
        overflow: auto;
    }
    
    .header {
        display: flex;
        flex-direction: column;
        align-items: center;
        position: relative;
        margin-top: 10px;
    }
    
    .content {
        width: 100%;
        min-height: 3em;
        padding: 10px;
        border-radius: 5px;
        background-color: gray;
        box-sizing: border-box;
    }
    
    .content p {
        margin: 0 0 10px 0;
    }
    
    .content p:last-of-type {
        margin: 0;
    }
    
    .footer {
        border-radius: 10px 10px 0 0;
        background-color: lightgray;
        display: flex;
        flex-direction: column;
        margin: 0 20px;
        padding: 10px;
    }
    
    .footer .info {
        display: flex;
        justify-content: flex-end;
        margin-bottom: 10px;
    }

    .footer .info button {
        margin: 0;
        padding: 0;
        border: 0;
        background: transparent;
    }

    .footer .info button:focus {
        outline: none;
    }

    .footer .info button:hover {
        cursor: pointer;
    }

    .footer .info button img {
        width: 32px;
        user-select: none;
    }

    .info-dialog {
        top: 50%;
        left: 50%;
        margin: 0;
        transform: translate(-50%, -50%);
        border: 0;
        border-radius: 10px;
        background-color: lightgray;
    }

    .info-dialog h2 {
        margin-top: 0;
        text-align: center;
        text-transform: uppercase;
    }

    .info-dialog::backdrop {
        background-color: rgba(0, 0, 0, 0.5);
    }

    .footer .info-dialog button {
        padding: 10px 20px;
        background: black;
        color: white;
        border-radius: 5px;
        text-transform: uppercase;
        user-select: none;
    }

    .footer .info-dialog button:focus {
        outline: none;
    }

    .footer .info-dialog button:hover {
        background: #4d418b;
    }

    .controls {
        display: flex;
        flex-direction: column;
        margin: 1em 0;
        gap: 10px;
    }

    .control-icon {
        display: flex;
        align-items: center;
        gap: 10px;
    }

    .control-icon img {
        width: 64px;
        user-select: none;
    }

    .footer .buttons {
        min-height: 100px;
        display: flex;
        flex-direction: column;
        overflow: auto;
        padding-top: 10px;
        gap: 10px;
    }

    .footer .buttons div {
        flex-basis: 100%;
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
    }
    
    .footer .button {
        background-color: black;
        padding: 10px 20px;
        border-radius: 5px;
        text-transform: uppercase;
        cursor: pointer;
        display: inline-block;
        user-select: none;
    }

    .footer .button:first-child {
        margin-left: 0;
    }

    .footer .button:last-child {
        margin-right: 0;
    }
    
    .footer .button.active,
    .footer .button:hover {
        background-color: #4d418b;
    }
`;

/**
 * Represents the Canvas page
 */
export class CanvasComponent extends HTMLElement {
    /** Instance of the game event service */
    protected readonly _gameEventService: GameEventService = new GameEventService();
    /** Instance of the game route service */
    protected readonly _gameRouteService: GameRouteService = new GameRouteService();

    /** Current game state */
    protected _currentGameState?: DefaultGameState;
    /** Current active action button */
    protected _selectedActionButton?: ActionReference;
    /** Current active game object buttons */
    protected _selectedGameObjectButtons: Set<GameObjectReference> = new Set<GameObjectReference>();

    /** Current room name */
    private _currentRoomName?: string;

    /** Instance of the sound component to handle audio playback */
    private soundComponents: SoundComponents = new SoundComponents();

    /**
     * The "constructor" of a Web Component
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });

        // Listen for the Update Game State event
        this._gameEventService.addGameEventListener<UpdateGameStateEvent>(GameEvent.UpdateGameState, event => {
            this.updateGameState(event.state);
        });

        // Listen for changes in the SoundComponents
        this.soundComponents.addEventListener("volumechange", () => {
            console.log(`Volume changed to: ${this.soundComponents.volume}`);
        });

        this.soundComponents.addEventListener("musicenabledchange", () => {
            console.log(`Music enabled: ${this.soundComponents.isMusicEnabled}`);
        });

        void this.refreshGameState();
    }

    /**
     * Refresh the current game state
     */
    protected async refreshGameState(): Promise<void> {
        const state: GameState = await this._gameRouteService.getGameState();

        this.updateGameState(state);
    }

    /**
     * Update the canvas to the provided game state
     *
     * @param state Game state to update the canvas to
     */
    protected updateGameState(state: GameState): void {
        // Handle switching pages, if requested.
        if (state.type === "switch-page") {
            this._gameEventService.switchPage(state.page as Page);

            return;
        }

        this._gameEventService.switchPage(state.currentPage as Page);

        // Reset the component
        this._currentGameState = state;

        this._selectedActionButton = undefined;
        this._selectedGameObjectButtons.clear();

        // Refresh the web component
        this.render();

        // Check if the room has changed
        const newRoomName: string = state.roomName;
        if (newRoomName !== this._currentRoomName) {
            this._currentRoomName = newRoomName;
            this.playBackgroundMusic(state.roomBackgroundMusic ?? undefined);
        }
    }

    /**
     * Plays background music if a music file is provided and music is enabled.
     * If no music file is provided, it stops the background music.
     *
     * @param musicFile - The name of the music file to play. If not provided, the background music will be stopped.
     */
    private playBackgroundMusic(musicFile?: string): void {
        if (musicFile) {
            this.soundComponents.playBackgroundMusic(musicFile);
        }
        else {
            this.soundComponents.stopBackgroundMusic(); // Stop audio if no music file is provided
        }
    }

    /**
     * CSS styles that override the default styles
     */
    protected styles(): string {
        return "";
    }

    /**
     * Render the contents of this page
     */
    protected render(): void {
        if (!this.shadowRoot) {
            return;
        }

        const elements: HTMLElement[] = htmlArray`
            <style>
                ${defaultStyles}
            </style>

            <style>
                ${this.styles()}
            </style>

            ${this.renderBody()}
            ${this.soundComponents} <!-- Ensure SoundComponents is rendered -->
        `;

        while (this.shadowRoot.firstChild) {
            this.shadowRoot.firstChild.remove();
        }

        const roomImages: string[] | undefined = this._currentGameState?.roomImages;

        if (roomImages && roomImages.length > 0) {
            this.style.backgroundImage = `url("/assets/img/rooms/${roomImages[0]}.png")`;
        }

        this.shadowRoot.append(...elements);
    }

    /**
     * Render the wrapper element
     *
     * @returns HTML element of the wrapper
     */
    protected renderBody(): HTMLElement {
        return html`
            <div class="wrapper">
                ${this.renderTitle()}
                ${this.renderFooter()}
                <game-notepad></game-notepad>
            </div>
        `;
    }

    /**
     * Render the title element
     *
     * @returns String with raw HTML for the title element. Can be empty.
     */
    protected renderTitle(): string {
        const roomName: string | undefined = this._currentGameState?.roomName;

        if (roomName) {
            return `
            <div class="title">
                <svg xmlns="http://www.w3.org/2000/svg" width="300" height="100">
                    <rect x="0" y="0" width="300" height="100" rx="5" ry="5" fill="#0059a9"/>
                    <rect x="10" y="10" width="280" height="80" rx="10" ry="10" fill="none" stroke="white" stroke-width="5"/>

                    <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="white">
                          ${roomName}
                    </text>
                </svg>
            </div>
            `;
        }

        return "";
    }

    /**
     * Render the content element
     *
     * @returns String with raw HTML for the content element
     */
    protected renderContent(): string {
        return `
            <div class="content">
                <game-inventory></game-inventory>
                ${this.renderComponent()}
                ${this.renderContentText()}
            </div>
        `;
    }

    /**
     * Render the info element
     *
     * @returns String with raw HTML for the info element
     */
    private renderInfo(): HTMLElement {
        const closeButton: HTMLButtonElement = html`
            <button>
                Sluiten
            </button>
        ` as HTMLButtonElement;

        const infoDialog: HTMLDialogElement = html`
            <dialog class="info-dialog">
                <h2>Informatie</h2>
                <p>Loop je vast? Vraag Johannes voor wat tips!</p>
                <p><b>Controles</b></p>

                <div class="controls">
                    <div class="control-icon">
                        <img src="/assets/img/icons/i.png" />
                        <span>Inventory memu</span>
                    </div>

                    <div class="control-icon">
                        <img src="/assets/img/icons/esc.png" />
                        <span>Geluids instellingen</span>
                    </div>
                </div>
                ${closeButton}
            </dialog>
        ` as HTMLDialogElement;

        const infoButton: HTMLElement = html`
            <button id="info-button">
                <img src="/assets/img/icons/information.png" alt="Information" />
            </button>
        `;

        closeButton.addEventListener("click", () => {
            infoDialog.close();
        });

        infoButton.addEventListener("click", () => {
            infoDialog.showModal();
        });

        return html`
            <div class="info">
                ${infoDialog}
                ${infoButton}
            </div>
        `;
    }

    protected renderContentText(): string {
        const plugins: BBobPlugins = [presetHTML5()];

        return `
            <game-text>
                ${this._currentGameState?.text.map(text => `<p>${bbobHTML(text, plugins)}</p>`).join("") || ""}
            </game-text>
        `;
    }

    /**
     * Render a web component
     *
     * @returns String with raw HTML for the web component
     */
    protected renderComponent(): string {
        if (!this._currentGameState?.currentComponent) {
            return "";
        }

        return `
            <game-${this._currentGameState.currentComponent.tag}
                ${
                    Object.entries(this._currentGameState.currentComponent.attributes)
                        .map(
                            ([key, value]) => `${key}="${value}"`
                        )
                        .join(" ")
                }
            >
            </game-${this._currentGameState.currentComponent.tag}>
        `;
    }

    /**
     * Render the footer element
     *
     * @returns HTML element of the footer
     */
    protected renderFooter(): HTMLElement {
        return html`
            <div class="footer">
                ${this.renderInfo()}
                ${this.renderContent()}
                ${this.renderFooterButtons()}
            </div>
        `;
    }

    protected renderFooterButtons(): HTMLElement {
        return html`
            <div class="buttons">
                <div>
                    ${this._currentGameState?.actions.map(button => this.renderActionButton(button))}
                </div>
                <div>
                    ${this._selectedActionButton
                        ? this._currentGameState?.objects
                            /** Only show game objects that are interactable with the selected action */
                            .filter(object =>
                                this._selectedActionButton?.interactableObjects?.includes(object.alias)
                            )
                            .map(button => this.renderGameObjectButton(button)) || ""
                        : ""
                    }
                </div>
            </div>
        `;
    }

    /**
     * Render an action button for a given action reference
     *
     * @returns HTML element of the action button
     */
    protected renderActionButton(button: ActionReference): HTMLElement {
        const element: HTMLElement = html`
            <a class="button ${this._selectedActionButton === button ? "active" : ""}">
                ${button.name}
            </a>
        `;

        element.addEventListener("click", () => this.handleClickAction(button));

        return element;
    }

    /**
     * Render a game object button for a given game object reference
     *
     * @returns HTML element of the game object button
     */
    protected renderGameObjectButton(button: GameObjectReference): HTMLElement {
        const element: HTMLElement = html`
            <a class="button ${this._selectedGameObjectButtons.has(button) ? "active" : ""}">
                ${button.name}
            </a>
        `;

        element.addEventListener("click", () => this.handleClickGameObject(button));

        return element;
    }

    /**
     * Handle the click on an action button
     *
     * @param button Action button that was clicked
     */
    protected async handleClickAction(button: ActionReference): Promise<void> {
        if (!this.shadowRoot) {
            return;
        }

        // If this actions needs a game object, show the available game objects.
        if (button.needsObject) {
            this._selectedActionButton = button;
            this._selectedGameObjectButtons.clear();

            // Rerender the Footer only
            this.shadowRoot.querySelector(".footer .buttons")!.replaceWith(this.renderFooterButtons());

            return;
        }

        // Otherwise, execute the action and update the game state.
        const state: GameState | undefined = await this._gameRouteService.executeAction(button.alias);

        if (state === undefined) {
            return;
        }

        this.updateGameState(state);
    }

    /**
     * Handle the click on a game object button
     *
     * @param button Game object button that was clicked
     */
    protected async handleClickGameObject(button: GameObjectReference): Promise<void> {
        // If no action button was clicked, do not try to handle this click.
        if (!this._selectedActionButton) {
            return;
        }

        // Add the game object to list of selected game objects
        this._selectedGameObjectButtons.add(button);

        // Try to execute the action with all game objects on the list
        const state: GameState | undefined = await this._gameRouteService.executeAction(
            this._selectedActionButton.alias,
            [...this._selectedGameObjectButtons].map(e => e.alias)
        );

        // If 2 more game objects where on the list, clear it.
        if (this._selectedGameObjectButtons.size >= 2) {
            this._selectedActionButton = undefined;
            this._selectedGameObjectButtons.clear();
        }

        // Refresh the web component
        this.render();

        // If no state was returned, exit silently. This can happen when an action needs more than 1 game object.
        if (state === undefined) {
            return;
        }

        // Otherwise, update the game state.
        this.updateGameState(state);
    }
}
